// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/lib/loginTargets.tsx
// v1.4.0 - System-Verwaltung nur am Desktop (Inhaber 22.10.2026: "klappt ja ohnehin nur ueber Desktop ... ueber Mobile
//           oder Tablet ... dürfte die dort gar nicht erst erscheinen"): Desktop = feiner Zeiger (Maus/Trackpad) und
//           mindestens 1024 px breit; Handy und Tablet (Finger) sehen nur ihre Firmen.
// v1.3.0 - Firma aus dem Link /login?firm=<kurzname> (Inhaber 21.10.2026): gemerkt fuer
//          diesen Tab, nach der Anmeldung geht es direkt in diese Firma, wenn die Person dort Zugang hat (preferFirm).
// v1.2.0 - Projekt aus dem Einladungslink (/login?invite=...&project=<id>, B-6): nach Passwort + PIN direkt dorthin
//          (myest/dashboard?project=). Gemerkt in sessionStorage, weil das Fenster die Adresse unterwegs aendern kann.
// v1.1.0 - Vor dem Wechsel nach MyEST: handOverPasscodePage (@estata/ui) - steht die PIN-Seite mit vollen Punkten, zeigt
//          MyEST sie einmal weiter und blendet sie ueber dem Dashboard aus; sonst (Passwort-Anmeldung) nichts.
// v1.0.1 - Endung .tsx (CRA-Resolver loest extension-less Imports hier auf
//          .tsx auf, nicht .ts). Inhalt unveraendert, kein JSX.
// v1.0.0 - Geteilte Login-Ziel-Logik fuer LoginModal + Login-Seite.
//          Baut die Ziel-Liste aus dem auth/check-User und navigiert dorthin.
//          'system' ist kein Tenant-Ziel; System-Zugang haengt an der Rolle
//          platform_admin (bzw. system.access), nicht am Tenant-Eintrag.
import axios from 'axios';
import { handOverPasscodePage } from '@estata/ui';

export interface LoginTarget {
  kind: 'tenant' | 'system';
  label: string;
  tenantId?: string;
  // Subdomain der Firma (<slug>.app.estata.com)
  slug?: string;
}

/** Desktop-Rechner: Maus oder Trackpad und breit genug - die System-Plattform ist nur dafuer gebaut. */
export function isDesktopDevice(): boolean {
  try {
    return window.matchMedia('(pointer: fine)').matches && window.innerWidth >= 1024;
  } catch {
    return true;
  }
}

// Baut die Ziel-Liste aus dem auth/check-User. System-Verwaltung nur am Desktop.
export function buildTargets(u: any, desktop: boolean = isDesktopDevice()): LoginTarget[] {
  const list: LoginTarget[] = [];
  const tenants: any[] = u?.tenants || [];
  for (const t of tenants) {
    if (t?.name === 'system') continue; // system ist kein Tenant-Ziel
    list.push({ kind: 'tenant', label: t.name, tenantId: t.id, slug: t.slug || undefined });
  }
  const isPlatformAdmin =
    u?.permissions?.includes('system.access') ||
    u?.roles?.includes('platform_admin');
  if (isPlatformAdmin && desktop) {
    list.push({ kind: 'system', label: 'System-Verwaltung' });
  }
  return list;
}

const FIRM_KEY = 'estata.firm';
const FIRM_SLUG = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;

/** Firma aus ?firm= merken (nur eine gueltige Subdomain) - gilt fuer diesen Tab, auch ueber Passcode und Schritte. */
export function rememberFirm(slug: string | null | undefined): string | null {
  const clean = (slug || '').trim().toLowerCase();
  if (!FIRM_SLUG.test(clean)) return null;
  try { sessionStorage.setItem(FIRM_KEY, clean); } catch { /* ohne Speicher: nur dieser Aufruf */ }
  return clean;
}

export function rememberedFirm(): string | null {
  try {
    const value = sessionStorage.getItem(FIRM_KEY);
    return value && FIRM_SLUG.test(value) ? value : null;
  } catch { return null; }
}

/** Hat die Person Zugang zur gemerkten Firma, ist sie das einzige Ziel (direkt hinein) - sonst bleibt die Liste.
 *  Ob es die Firma gibt oder ob die Person dort ist, sagt die Seite nie. */
export function preferFirm(list: LoginTarget[], firm: string | null = rememberedFirm()): LoginTarget[] {
  if (!firm) return list;
  const match = list.find((target) => target.kind === 'tenant' && target.slug === firm);
  return match ? [match] : list;
}

const NEXT_PROJECT_KEY = 'estata.nextProject';
const PROJECT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Projekt aus dem Link merken - nur eine UUID, sonst nichts (kein offener Weiterleiter). */
export function rememberNextProject(projectId: string | null | undefined): void {
  if (!projectId || !PROJECT_ID.test(projectId)) return;
  try { sessionStorage.setItem(NEXT_PROJECT_KEY, projectId); } catch { /* ohne Speicher: Startseite */ }
}

/** Wohin in MyEST: mit gemerktem Projekt die Startseite mit ?project=, sonst die Startseite. Einmalig. */
export function myestEntry(): string {
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(NEXT_PROJECT_KEY);
    sessionStorage.removeItem(NEXT_PROJECT_KEY);
  } catch { /* ohne Speicher */ }
  return id && PROJECT_ID.test(id) ? `/myest/dashboard?project=${id}` : '/myest/';
}

// Navigiert zum gewaehlten Ziel. Tenant -> erst switch-tenant, dann myest.
// System -> direkt; das system-Frontend (App.tsx) switcht den Kontext selbst.
export async function goToTarget(target: LoginTarget): Promise<void> {
  try {
    if (target.kind === 'tenant' && target.tenantId) {
      await axios.post('/system/api/v1/auth/switch-tenant',
        { tenant_id: target.tenantId },
        { withCredentials: true });
      handOverPasscodePage();
      window.location.replace(myestEntry());
    } else {
      window.location.replace('/system/');
    }
  } catch {
    // Switch fehlgeschlagen: trotzdem navigieren, myest faellt sonst auf
    // Default-Tenant zurueck — besser als haengende UI.
    if (target.kind !== 'system') handOverPasscodePage();
    window.location.replace(target.kind === 'system' ? '/system/' : myestEntry());
  }
}
